import { SubdirectoryArrowRight } from "@mui/icons-material"
import { Chip, Collapse, List, ListItemButton, ListItemIcon, ListItemText } from "@mui/material"
import { useAnalytics } from "connected-components/analytics"
import PropTypes from "prop-types"
import { useCallback, useEffect, useRef, useState } from "react"
import { AI_TEAMMATE_EVENTS } from "../../analytics/events"
import { PROMPT_CATEGORIES, STARTER_PROMPTS } from "../../helpers/constants"
import { useMessagesContext } from "../../hooks/context"
import { useShouldDisableUserInput } from "../../hooks/selectors"

export default function SuggestedPrompts({ onPromptSelected, surface = "empty_state" } = {}) {
  const { dispatchUserMessageInputOverride, focusUserMessageInput } = useMessagesContext()
  const isDisabled = useShouldDisableUserInput()
  const analytics = useAnalytics()
  const [expandedCategory, setExpandedCategory] = useState(null)
  const [lastExpandedCategoryTheme, setLastExpandedCategoryTheme] = useState(null)
  const firedShownRef = useRef(new Set())

  useEffect(() => {
    STARTER_PROMPTS.forEach(({ id, theme }) => {
      if (!firedShownRef.current.has(id)) {
        firedShownRef.current.add(id)
        analytics.track(AI_TEAMMATE_EVENTS.SUGGESTED_PROMPT_SHOWN, { theme, prompt_id: id, surface })
      }
    })
  }, [analytics, surface])

  const handlePromptClick = useCallback((prompt) => {
    analytics.track(AI_TEAMMATE_EVENTS.SUGGESTED_PROMPT_CLICKED, { theme: prompt.theme, prompt_id: prompt.id, surface })
    dispatchUserMessageInputOverride(prompt.text, { source: "suggested_prompt", promptId: prompt.id, theme: prompt.theme })
    focusUserMessageInput()
    onPromptSelected?.()
  }, [analytics, dispatchUserMessageInputOverride, focusUserMessageInput, onPromptSelected, surface])

  const handleCategoryClick = useCallback((theme) => {
    const next = expandedCategory === theme ? null : theme
    setLastExpandedCategoryTheme(theme)
    setExpandedCategory(next)
    if (next) {
      analytics.track(AI_TEAMMATE_EVENTS.CATEGORY_EXPANDED, { theme })
      const category = PROMPT_CATEGORIES.find((c) => c.theme === theme)
      category?.prompts.forEach(({ id, theme: promptTheme }) => {
        if (!firedShownRef.current.has(id)) {
          firedShownRef.current.add(id)
          analytics.track(AI_TEAMMATE_EVENTS.SUGGESTED_PROMPT_SHOWN, { theme: promptTheme, prompt_id: id, surface })
        }
      })
    }
  }, [analytics, expandedCategory, surface])

  const expandedCategoryData = PROMPT_CATEGORIES.find((c) => c.theme === expandedCategory) ?? null
  // Retain last value so children stay rendered during Collapse's close animation.
  const lastExpandedCategoryData = PROMPT_CATEGORIES.find((c) => c.theme === lastExpandedCategoryTheme) ?? null
  const categoryDataForRender = expandedCategoryData ?? lastExpandedCategoryData

  return (
    <div className="suggested-prompts">
      <span className="suggested-prompts-label">Try asking...</span>
      <List dense disablePadding>
        {STARTER_PROMPTS.map((prompt) => (
          <ListItemButton
            key={prompt.id}
            disabled={isDisabled}
            onClick={() => handlePromptClick(prompt)}
            className="suggested-prompts-item"
          >
            <ListItemIcon className="suggested-prompts-icon">
              <SubdirectoryArrowRight fontSize="small" />
            </ListItemIcon>
            <ListItemText primary={prompt.text} />
          </ListItemButton>
        ))}
      </List>
      <div className="suggested-prompts-categories">
        <span className="suggested-prompts-label suggested-prompts-categories-label">Browse topics</span>
        <div className="category-chips">
          {PROMPT_CATEGORIES.map((category) => (
            <Chip
              key={category.theme}
              label={category.label}
              size="small"
              color={expandedCategory === category.theme ? "primary" : "default"}
              variant={expandedCategory === category.theme ? "filled" : "outlined"}
              onClick={() => handleCategoryClick(category.theme)}
              disabled={isDisabled}
            />
          ))}
        </div>
        <Collapse in={!!expandedCategoryData} unmountOnExit>
          {categoryDataForRender && (
            <List dense disablePadding className="category-prompts">
              {categoryDataForRender.prompts.map((prompt) => (
                <ListItemButton
                  key={prompt.id}
                  disabled={isDisabled}
                  onClick={() => handlePromptClick(prompt)}
                  className="suggested-prompts-item"
                >
                  <ListItemIcon className="suggested-prompts-icon">
                    <SubdirectoryArrowRight fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary={prompt.text} />
                </ListItemButton>
              ))}
            </List>
          )}
        </Collapse>
      </div>
    </div>
  )
}

SuggestedPrompts.propTypes = {
  onPromptSelected: PropTypes.func,
  surface: PropTypes.oneOf(["empty_state", "collapsed"]),
}
